<template>
  <div v-if="mixinUserInRoles(['Admin', 'Finance'])" class="container py-5 accounts-payable-generator">
    <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
      <div>
        <h1 class="h2 mb-1">Accounts Payable</h1>
        <p class="text-muted mb-0">Preview eligible vendor jobs before creating or updating AP invoices.</p>
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <form class="ap-generator-form" @submit.prevent="preview">
          <div>
            <label class="form-label" for="ap-invoice-date">Invoice month</label>
            <input id="ap-invoice-date" v-model="invoiceDate" type="date" class="form-control" required>
            <div class="form-text">The invoice date is normalized to the last day of this month.</div>
          </div>
          <div>
            <label class="form-label" for="ap-jobs-limit-date">Include completed jobs through</label>
            <input id="ap-jobs-limit-date" v-model="jobsLimitDate" type="date" class="form-control" required>
          </div>
          <div class="d-flex flex-wrap gap-2 pt-1">
            <button type="submit" class="btn btn-outline-primary" :disabled="loading">
              {{ loading && activeAction === 'preview' ? 'Previewing…' : 'Preview' }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canGenerate || loading"
              @click="generate"
            >
              {{ loading && activeAction === 'generate' ? 'Generating…' : 'Generate APs' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
    <div v-if="successMessage" class="alert alert-success" role="alert">{{ successMessage }}</div>

    <template v-if="plan">
      <div class="alert alert-light border d-flex flex-wrap gap-4" role="status">
        <span><strong>Invoice date:</strong> {{ formatDate(plan.invoiceDate) }}</span>
        <span><strong>Jobs:</strong> {{ plan.summary.jobs }}</span>
        <span><strong>Vendors:</strong> {{ plan.summary.vendors }}</span>
        <span
          class="ap-tooltip-target"
          data-ap-tooltip
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="actionTooltip('create')"
          tabindex="0"
        ><strong>Create:</strong> {{ plan.summary.create }}</span>
        <span
          class="ap-tooltip-target"
          data-ap-tooltip
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="actionTooltip('update')"
          tabindex="0"
        ><strong>Update:</strong> {{ plan.summary.update }}</span>
        <span
          class="ap-tooltip-target"
          data-ap-tooltip
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="actionTooltip('unchanged')"
          tabindex="0"
        ><strong>Unchanged:</strong> {{ plan.summary.unchanged }}</span>
        <span
          class="ap-tooltip-target"
          data-ap-tooltip
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="secureLinksTooltip"
          tabindex="0"
        ><strong>Secure links:</strong> {{ plan.summary.secureLinksToWrite }}</span>
      </div>

      <div v-if="plan.summary.conflicts" class="alert alert-danger">
        {{ plan.summary.conflicts }} vendor(s) have duplicate unpaid AP records. Resolve those records in Airtable before generating.
      </div>
      <div v-if="plan.summary.skippedJobs" class="alert alert-warning">
        {{ plan.summary.skippedJobs }} malformed job(s) were skipped. Generation is blocked until every skipped job is corrected.
        <details class="mt-2">
          <summary>Skipped jobs</summary>
          <ul class="mb-0 mt-2">
            <li v-for="job in plan.skippedJobs" :key="job.jobId">
              <template v-if="job.jobNumber">Job #{{ job.jobNumber }}</template>
              <template v-else>{{ job.jobId }}</template>
              — {{ job.reason }}
            </li>
          </ul>
        </details>
      </div>

      <div class="table-responsive border rounded">
        <table class="table table-striped align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Vendor</th>
              <th class="text-end">Jobs</th>
              <th>Action</th>
              <th>AP</th>
              <th>Send</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in displayItems" :key="item.vendorName + item.accountsPayableId">
              <td>{{ item.vendorName }}</td>
              <td class="text-end">{{ item.jobCount ?? item.jobIds.length }}</td>
              <td>
                <span
                  class="badge ap-tooltip-target"
                  :class="actionClass(item.action)"
                  data-ap-tooltip
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="actionTooltip(item.action, item)"
                  tabindex="0"
                >{{ item.action }}</span>
              </td>
              <td>
                <a v-if="itemLink(item)" :href="itemLink(item)" target="_blank" rel="noopener noreferrer">
                  {{ item.accountsPayableNumber ? `AP #${item.accountsPayableNumber}` : 'Open invoice' }}
                </a>
                <span v-else-if="item.accountsPayableNumber">AP #{{ item.accountsPayableNumber }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!canSend(item) || sendingId === item.accountsPayableId"
                  :title="sendButtonTitle(item)"
                  @click="sendInvoice(item)"
                >
                  {{ sendingId === item.accountsPayableId ? 'Sending…' : 'Send AP' }}
                </button>
              </td>
            </tr>
            <tr v-if="displayItems.length === 0">
              <td colspan="5" class="text-center text-muted py-4">No eligible jobs found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>

  <div v-else class="container py-5">
    <div class="alert alert-danger">Admin or Finance access is required.</div>
  </div>
</template>

<script>
import {
  generateAccountsPayable,
  previewAccountsPayable,
  sendAccountsPayable,
} from '@/services/accountsPayable';

function todayIso() {
  const today = new Date();
  const local = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export default {
  name: 'AccountsPayable',

  data() {
    const today = todayIso();
    return {
      invoiceDate: today,
      jobsLimitDate: today,
      plan: null,
      generatedResults: null,
      loading: false,
      activeAction: '',
      errorMessage: '',
      successMessage: '',
      sendingId: '',
      tooltipInstances: [],
    };
  },

  computed: {
    canGenerate() {
      return this.plan
        && this.plan.summary.conflicts === 0
        && this.plan.summary.skippedJobs === 0
        && (this.plan.summary.create > 0 || this.plan.summary.update > 0);
    },

    displayItems() {
      return this.generatedResults || this.plan?.items || [];
    },

    secureLinksTooltip() {
      return 'Number of invoice links that will be created or corrected when “Generate APs” runs.';
    },
  },

  watch: {
    invoiceDate() { this.clearPreview(); },
    jobsLimitDate() { this.clearPreview(); },
  },

  methods: {
    clearPreview() {
      this.disposeTooltips();
      this.plan = null;
      this.generatedResults = null;
      this.successMessage = '';
    },

    getErrorMessage(error) {
      return error.response?.data?.error || error.message || 'Accounts payable request failed.';
    },

    async run(action, request) {
      this.loading = true;
      this.activeAction = action;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        return await request();
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
        return null;
      } finally {
        this.loading = false;
        this.activeAction = '';
      }
    },

    async preview() {
      const result = await this.run('preview', () => (
        previewAccountsPayable(this.invoiceDate, this.jobsLimitDate)
      ));
      if (result) {
        this.plan = result;
        this.generatedResults = null;
        this.refreshTooltips();
      }
    },

    async generate() {
      if (!this.canGenerate) return;
      const confirmed = window.confirm(
        `Generate ${this.plan.summary.create} new and update ${this.plan.summary.update} existing AP invoice(s)?`,
      );
      if (!confirmed) return;

      const result = await this.run('generate', () => (
        generateAccountsPayable(this.invoiceDate, this.jobsLimitDate)
      ));
      if (result) {
        this.plan = result;
        this.generatedResults = result.results;
        this.successMessage = 'Accounts payable generation completed. Repeating it with the same data will not create duplicates.';
        this.refreshTooltips();
      }
    },

    formatDate(value) {
      if (!value) return '';
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
      }).format(new Date(`${value}T00:00:00Z`));
    },

    actionClass(action) {
      return {
        create: 'bg-success',
        update: 'bg-warning text-dark',
        unchanged: 'bg-secondary',
        conflict: 'bg-danger',
      }[action] || 'bg-secondary';
    },

    itemLink(item) {
      return item.publicUrl || item.expectedPublicUrl || '';
    },

    canSend(item) {
      if (!item.accountsPayableId || !item.vendorEmail) return false;
      if (this.generatedResults) return true;
      return item.action === 'unchanged';
    },

    sendButtonTitle(item) {
      if (!item.accountsPayableId) return 'Run “Generate APs” before sending this invoice.';
      if (!item.vendorEmail) return 'This vendor does not have a valid email address.';
      if (!this.generatedResults && item.action !== 'unchanged') {
        return 'Run “Generate APs” first so the invoice contains the current jobs and link.';
      }
      return `Send this AP to ${item.vendorEmail}.`;
    },

    async sendInvoice(item) {
      if (!this.canSend(item)) return;

      const apLabel = item.accountsPayableNumber
        ? `AP #${item.accountsPayableNumber}`
        : 'this AP';
      const confirmed = window.confirm(
        `Send ${apLabel} to ${item.vendorName} at ${item.vendorEmail}?`,
      );
      if (!confirmed) return;

      this.sendingId = item.accountsPayableId;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const result = await sendAccountsPayable(item.accountsPayableId);
        item.emailSentAt = result.sentAt;
        this.successMessage = `${apLabel} was sent to ${result.recipient}.`;
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.sendingId = '';
      }
    },

    actionTooltip(action, item = null) {
      const apLabel = item?.accountsPayableNumber
        ? `AP #${item.accountsPayableNumber}`
        : 'An AP';

      return {
        create: 'No AP exists yet for this vendor and invoice month. “Generate APs” will create a new invoice.',
        update: `${apLabel} already exists for this vendor and invoice month, but its eligible jobs or link details have changed. “Generate APs” will refresh the same AP; it will not create another one.`,
        unchanged: 'The existing AP already contains the current eligible jobs and working invoice link. “Generate APs” will leave it as it is.',
        conflict: 'More than one editable AP exists for this vendor and invoice month. Resolve the duplicate records in Airtable before generating.',
      }[action] || 'This is the action the generator will take for this vendor.';
    },

    disposeTooltips() {
      this.tooltipInstances.forEach((tooltip) => tooltip.dispose());
      this.tooltipInstances = [];
    },

    refreshTooltips() {
      this.$nextTick(() => {
        this.disposeTooltips();
        const Tooltip = window.bootstrap?.Tooltip;
        if (!Tooltip) return;

        this.tooltipInstances = Array.from(
          this.$el.querySelectorAll('[data-ap-tooltip]'),
        ).map((element) => new Tooltip(element, {
          container: 'body',
          customClass: 'ap-action-tooltip',
          trigger: 'hover focus',
        }));
      });
    },
  },

  beforeUnmount() {
    this.disposeTooltips();
  },
};
</script>

<style scoped>
.accounts-payable-generator {
  max-width: 1100px;
}

.ap-generator-form {
  display: grid;
  gap: 1rem;
  max-width: 34rem;
}

.ap-tooltip-target {
  cursor: help;
}

:global(.ap-action-tooltip .tooltip-inner) {
  max-width: 360px;
  padding: 0.7rem 0.9rem;
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: left;
}
</style>
